import CloseIcon from "@mui/icons-material/Close";
import React from "react";
import _ from "lodash";

import {
  Box,
  Chip,
  Divider,
  IconButton,
  Modal,
  Typography,
} from '@mui/material';

// Styles for the modal
const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  minWidth: 800,
  maxWidth: '70vw',
  maxHeight: '90vh',
  bgcolor: 'background.paper',
  border: '1px solid #e0e0e0',
  borderRadius: '12px',
  boxShadow: 24,
  p: 4,
  display: 'flex',
  flexDirection: 'column',
  outline: 'none',
};

/**
 * Renders a modal to display detailed questions and answers for a selected topic.
 *
 * @param {object} props - The component props.
 * @param {boolean} props.open - Controls the visibility of the modal.
 * @param {function} props.onClose - Function to call when the modal is requested to close.
 * @param {string} props.title - The title of the modal (e.g., the topic name).
 * @param {Array<object>} props.contentDetails - An array of objects, where each object contains:
 * - question: string
 * - answer: string
 * - student_count: number
 * - is_assessment_related: boolean
 */
const TopicDetailsModal = ({ open, onClose, title, contentDetails }) => {
  // Sort contentDetails by student_count in descending order
  const sortedContentDetails = _.sortBy(
    contentDetails,
    (item) => -item.student_count,
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="topic-details-title"
      aria-describedby="topic-details-description"
    >
      <Box sx={modalStyle}>
        {/* Header Section */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography
            id="topic-details-title"
            variant="h5"
            component="h2"
            sx={{ flexGrow: 1, pr: 6, fontWeight: 600 }}
          >
            {title}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              'color': (theme) => theme.palette.grey[600],
              '&:hover': {
                backgroundColor: (theme) => theme.palette.grey[100],
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Scrollable content area */}
        <Box sx={{ overflowY: 'auto', flexGrow: 1, pr: 2 }}>
          {sortedContentDetails.length > 0 ? (
            sortedContentDetails.map((item, index) => (
              <Box
                key={index}
                sx={{
                  'mb': 3,
                  'p': 2,
                  'border': '1px solid #f0f0f0',
                  'borderRadius': '8px',
                  'backgroundColor': '#fafafa',
                  'boxShadow': '0 1px 3px rgba(0,0,0,0.05)',
                  '&:last-child': { mb: 0 },
                }}
              >
                <Typography
                  variant="subtitle1"
                  component="div"
                  sx={{ mb: 1, fontWeight: 600 }}
                >
                  {`Question ${index + 1}:`}
                  <Box
                    component="span"
                    sx={{ ml: 1, fontWeight: 400, color: 'text.primary' }}
                  >
                    {item.question}
                  </Box>
                </Typography>
                <Typography variant="body1" component="div" sx={{ mb: 1 }}>
                  <Typography component="span" sx={{ fontWeight: 600 }}>
                    Answer:
                  </Typography>{' '}
                  {item.answer}
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  {/* <Chip
                    label={`No of Students Asked: ${item.student_count}`}
                    size="small"
                    // Modified styling for 50% opacity background, 100% color border, and rounded corners
                    sx={(theme) => ({
                      fontWeight: 400,
                      backgroundColor: `${'#9397FF'}20`, // 80 for 50% opacity
                      color: theme.palette.info.contrastText,
                      color: 'black',
                      border: `1px solid ${'#9397FF'}`, // Solid border with full color
                      //   borderRadius: '50%', // Perfectly rounded
                    })}
                  /> */}
                  {item.is_assessment_related && (
                    <Chip
                      label="Assessment Related"
                      size="small"
                      // Modified styling for 50% opacity background, 100% color border, and rounded corners
                      sx={(theme) => ({
                        fontWeight: 400,
                        backgroundColor: `${'#A2EAB3'}20`, // 80 for 50% opacity
                        color: theme.palette.warning.contrastText,
                        color: 'black',

                        border: `1px solid ${'#4dc268'}`, // Solid border with full color
                        // borderRadius: '50%', // Perfectly rounded
                      })}
                    />
                  )}
                </Box>
              </Box>
            ))
          ) : (
            <Typography
              sx={{ mt: 2, color: 'text.secondary', textAlign: 'center' }}
            >
              No detailed information available for this topic.
            </Typography>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default TopicDetailsModal;
